import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CheckCircle2,
  Mail,
  MapPin,
  Calendar,
  ArrowRight,
  FileText,
  Printer,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { BRAND_INFO, CONTACT_INFO } from '../data/mock';
import { useCart } from '../context/CartContext';

const LAB_NAMES = {
  labcorp: 'LabCorp',
  quest: 'Quest Diagnostics',
  bioreference: 'BioReference',
};

const OrderConfirmationPage = () => {
  const { id } = useParams();
  usePageTitle(`Order confirmed — ${id} | ${BRAND_INFO.name}`);
  const { clear, items: cartItems } = useCart();
  const [order, setOrder] = useState(null);

  // Hydrate order from sessionStorage and clear the cart on mount
  useEffect(() => {
    let snapshot = null;
    try {
      const raw = sessionStorage.getItem(`as_order_${id}`);
      if (raw) snapshot = JSON.parse(raw);
    } catch (e) {
      /* ignore */
    }
    setOrder(snapshot);
    // Clear cart only if we still had items (avoid double-clear on revisit)
    if (cartItems.length > 0) clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!order) {
    return (
      <>
        <Header />
        <section className="pt-28 lg:pt-36 pb-24 bg-cream-100 min-h-[60vh]">
          <div className="container-tight text-center">
            <p className="eyebrow justify-center">Order</p>
            <h1 className="mt-4 font-display text-display-md text-ink-900">
              Looking for order {id}?
            </h1>
            <p className="mt-3 text-ink-600">
              We couldn&apos;t find a confirmation in this browser session. Check your email
              for the order receipt, or reach out to {CONTACT_INFO.email} and we&apos;ll help.
            </p>
            <Link to="/panels" className="btn-brick mt-7 inline-flex">
              Back to lab panels <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  const { form, items, subtotal, fee, tax, total, createdAt } = order;
  const created = new Date(createdAt);

  return (
    <>
      <Header />

      {/* Confirmation banner */}
      <section className="pt-28 lg:pt-36 pb-12 bg-cream-100">
        <div className="container-wide">
          <div className="bg-cream-50 border border-cream-200 rounded-3xl p-8 lg:p-10">
            <div className="flex items-start gap-4">
              <span className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full bg-sage-500 text-white">
                <CheckCircle2 className="w-6 h-6" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="eyebrow">Order confirmed</p>
                <h1 className="mt-3 font-display text-display-lg text-ink-900">
                  Thanks, {form.firstName}.{' '}
                  <em className="italic text-brick-500">Your requisition is on its way.</em>
                </h1>
                <p className="mt-3 text-ink-600 max-w-2xl">
                  We sent a copy of your physician-signed lab requisition to{' '}
                  <span className="text-ink-900 font-medium">{form.email}</span>. Walk
                  in to any participating {LAB_NAMES[form.lab]} draw center near you. You can also
                  pull up the requisition on your phone at the counter — no printout required.
                </p>
              </div>
            </div>

            <dl className="mt-8 grid sm:grid-cols-3 gap-4 pt-6 border-t border-cream-200">
              <div>
                <dt className="text-[10px] uppercase tracking-[0.18em] text-ink-400">Order ID</dt>
                <dd className="mt-1 font-mono text-ink-900 text-[15px] tabular-nums">{order.id}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-[0.18em] text-ink-400">Placed</dt>
                <dd className="mt-1 text-ink-900 text-[15px]">
                  {created.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}{' '}
                  ·{' '}
                  {created.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-[0.18em] text-ink-400">Total charged</dt>
                <dd className="mt-1 font-display text-2xl text-ink-900 tabular-nums">
                  ${total.toFixed(2)} <span className="text-[12px] text-ink-500">USD</span>
                </dd>
              </div>
            </dl>

            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href={`mailto:${form.email}?subject=My ${BRAND_INFO.name} requisition&body=My order id is ${order.id}`}
                className="btn-brick"
              >
                <Mail className="w-4 h-4" /> Email me the requisition
              </a>
              <button onClick={() => window.print()} className="btn-light">
                <Printer className="w-4 h-4" /> Print this page
              </button>
              <Link to="/panels" className="btn-ghost">
                Order another panel →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What happens next */}
      <section className="pb-16 bg-cream-100">
        <div className="container-wide">
          <h2 className="font-display text-display-md text-ink-900">
            What happens next.
          </h2>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {[
              {
                step: '01',
                title: 'Check your inbox',
                body: `Your physician-signed requisition has been emailed to ${form.email}. It usually arrives within a few minutes.`,
              },
              {
                step: '02',
                title: `Walk in to ${LAB_NAMES[form.lab]}`,
                body: 'No appointment is required for most locations. Bring photo ID and the requisition (digital or printed).',
              },
              {
                step: '03',
                title: 'Watch your dashboard',
                body: 'As the lab releases markers, they stream into your timeline. Most panels finish within 48 hours.',
              },
            ].map((s) => (
              <article key={s.step} className="bg-cream-50 border border-cream-200 rounded-3xl p-7">
                <div className="font-display text-4xl text-brick-500 leading-none">{s.step}</div>
                <h3 className="mt-3 font-display text-2xl text-ink-900">{s.title}</h3>
                <p className="mt-3 text-ink-600 text-[15px] leading-relaxed">{s.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Order details */}
      <section className="pb-24 bg-cream-100">
        <div className="container-wide grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 bg-cream-50 border border-cream-200 rounded-3xl p-8">
            <h3 className="font-display text-2xl text-ink-900">Order details</h3>
            <ul className="mt-5 divide-y divide-cream-200">
              {items.map((it) => (
                <li key={it.id} className="py-4 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-ink-400">
                      {it.kind === 'panel' ? `Panel · ${it.sku || ''}` : 'Custom panel'}
                    </div>
                    <div className="font-display text-lg text-ink-900">{it.name}</div>
                    {it.kind === 'custom' && it.markers && (
                      <div className="mt-1 text-[12px] text-ink-500">
                        {it.markers.map((m) => m.name).join(' · ')}
                      </div>
                    )}
                    <div className="mt-1 text-[12px] text-ink-500">
                      {it.qty > 1 ? `Qty ${it.qty} · ` : ''}${it.price.toFixed(2)} each
                    </div>
                  </div>
                  <div className="font-display text-xl text-ink-900 tabular-nums">
                    ${(it.price * (it.qty || 1)).toFixed(0)}
                  </div>
                </li>
              ))}
            </ul>
            <dl className="mt-6 pt-6 border-t border-cream-200 space-y-2 text-[14px]">
              <div className="flex justify-between text-ink-700">
                <dt>Subtotal</dt>
                <dd className="tabular-nums">${subtotal.toFixed(2)}</dd>
              </div>
              <div className="flex justify-between text-ink-700">
                <dt>Lab processing</dt>
                <dd className="tabular-nums">${fee.toFixed(2)}</dd>
              </div>
              <div className="flex justify-between text-ink-700">
                <dt>Tax</dt>
                <dd className="tabular-nums">${tax.toFixed(2)}</dd>
              </div>
              <div className="flex justify-between font-display text-2xl text-ink-900 pt-3 border-t border-cream-200">
                <dt>Total</dt>
                <dd className="tabular-nums">${total.toFixed(2)}</dd>
              </div>
            </dl>
          </div>

          <aside className="lg:col-span-4 space-y-5">
            <div className="bg-cream-50 border border-cream-200 rounded-3xl p-7">
              <h3 className="font-display text-xl text-ink-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-brick-500" /> Patient on the requisition
              </h3>
              <div className="mt-3 text-[14px] text-ink-800">
                {form.firstName} {form.lastName}
              </div>
              <div className="text-[12px] text-ink-500">
                DOB {form.dob} · {form.sex}
              </div>
              <div className="text-[12px] text-ink-500">{form.email} · {form.phone}</div>
            </div>
            <div className="bg-cream-50 border border-cream-200 rounded-3xl p-7">
              <h3 className="font-display text-xl text-ink-900 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-brick-500" /> Address
              </h3>
              <div className="mt-3 text-[14px] text-ink-800 leading-relaxed">
                {form.street}{form.street2 ? `, ${form.street2}` : ''}<br />
                {form.city}, {form.state} {form.zip}
              </div>
              <div className="mt-2 text-[12px] text-ink-500 capitalize">
                Preferred lab: {LAB_NAMES[form.lab]}
              </div>
            </div>
            <div className="bg-charcoal-900 text-cream-50 rounded-3xl p-7">
              <h3 className="font-display text-xl flex items-center gap-2">
                <Calendar className="w-4 h-4 text-brick-300" /> Need help?
              </h3>
              <p className="mt-3 text-[14px] text-cream-200/80 leading-relaxed">
                Reply to your confirmation email or reach our support team at{' '}
                <a className="underline" href={`mailto:${CONTACT_INFO.email}`}>
                  {CONTACT_INFO.email}
                </a>{' '}
                or {CONTACT_INFO.phone}.
              </p>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default OrderConfirmationPage;
